import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import FileFormatter from 'components/FileFormatter';
import TagColorFormatter from 'components/TagColorFormatter';
import { TagFormatter } from 'components/TagFormatter';
import Button from 'components/Button';
import PageHeader from 'components/styled/PageHeader';
import Text from 'components/styled/Text';
import PageHeaderContainer from 'components/styled/PageHeaderContainer';
import { BsArrowLeft } from 'react-icons/bs';
import { colors, FILE_TYPES, PAGE_PATHS, PRICE_TYPES } from '../../constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  categoriesSelector,
  productDraftSelector,
  productDraftValidationErrorsSelector,
} from 'store/selectors';
import { GridTable } from 'components/GridTable';
import { useReduxActions } from 'hooks/useReduxActions';
import DropDown from 'components/DropDown';
import EmptyCell from 'components/EmptyCell';
import { getColumnsWidth } from 'utils/helpers';

function ProductDraft() {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const { t } = useTranslation();
  const actions = useReduxActions();
  const productDraft = useSelector(productDraftSelector);
  const categories = useSelector(categoriesSelector);
  const productDraftValidationErrors = useSelector(
    productDraftValidationErrorsSelector,
  );

  const [currentOptionsDropDownIndex, setCurrentOptionsDropDownIndex] =
    useState({});
  const columnsWidth = getColumnsWidth();

  const columnDefs = useMemo(
    () => [
      {
        field: 'select',
        width: columnsWidth.select,
        maxWidth: 66,
        pinned: 'left',
        headerCheckboxSelection: true,
        checkboxSelection: true,
      },
      {
        headerName: t('product.draft.header.id'),
        maxWidth: 75,
        field: 'index',
        width: columnsWidth.index,
        pinned: 'left',
      },
      {
        headerName: t('product.draft.header.product.name'),
        field: 'product_name',
        editable: true,
        minWidth: 150,
        width: columnsWidth.product_name,
        pinned: 'left',
      },
      {
        headerName: t('product.draft.header.category'),
        field: 'category',
        width: columnsWidth.category,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              right={-60}
              items={categories.map(item => ({
                text: item.name,
                onClick: handleSelectOption(item, props),
              }))}>
              {props?.value?.name ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value?.name}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('product.draft.header.subcategory'),
        field: 'subcategory',
        width: columnsWidth.subcategory,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const category = categories.find(
            c => c.name === props.data.category?.name,
          );
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                category?.subcategory.map(item => ({
                  text: item.name,
                  onClick: handleSelectOption(item.name, props),
                })) || []
              }>
              {props.value ? (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {props?.value}
                </Text>
              ) : (
                <EmptyCell
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}
                />
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('product.draft.header.link'),
        field: 'link',
        width: columnsWidth.link,
        type: 'link',
        editable: true,
      },
      {
        headerName: t('product.draft.header.photo.video'),
        field: 'photo_video',
        width: columnsWidth.photo_video,
        minWidth: 150,
        cellRenderer: props => (
          <FileFormatter
            data={props.data.photo_video}
            editable
            type={FILE_TYPES.IMAGE_AND_VIDEO}
            rowId={props.data.row_id}
            recall={reFetch}
            model="product"
          />
        ),
      },
      {
        headerName: t('product.draft.header.color'),
        field: 'color',
        width: columnsWidth.color,
        minWidth: 250,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => (
          <TagColorFormatter
            rowIndex={props.rowIndex}
            data={props.data}
            editable
            onChangeRow={handleUpdateProductDraft}
          />
        ),
      },
      {
        headerName: t('product.draft.header.product.size'),
        field: 'product_size',
        width: columnsWidth.product_size,
        editable: true,
        type: 'tags',
        cellRenderer: props => (
          <TagFormatter
            data={props}
            editable
            onDeleteTag={handleUpdateProductDraft}
            field={props.colDef.field}
          />
        ),
      },
      {
        headerName: t('product.draft.header.product.weight'),
        field: 'product_weight',
        width: columnsWidth.product_weight,
        type: 'weight',
        minWidth: 150,
        editable: true,
      },
      {
        headerName: t('product.draft.header.description'),
        field: 'description',
        width: columnsWidth.description,
        minWidth: 150,
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
      },
      {
        headerName: t('product.draft.header.EXW'),
        field: 'EXW',
        width: columnsWidth.EXW,
        minWidth: 120,
        editable: true,
        type: 'chinaCurrency',
      },
      {
        headerName: t('product.draft.header.MOQ'),
        field: 'MOQ',
        width: columnsWidth.MOQ,
        minWidth: 120,
        editable: true,
      },
      {
        headerName: t('product.draft.header.price'),
        field: 'price',
        width: columnsWidth.price,
        type: 'USDTCurrency',
        minWidth: 150,
        editable: true,
      },
      {
        headerName: t('product.draft.header.quantity'),
        field: 'quantity',
        width: columnsWidth.quantity,
        minWidth: 120,
        editable: true,
      },
      {
        headerName: t('product.draft.header.10pcs'),
        field: '10pcs',
        width: columnsWidth['10pcs'],
        type: 'USDTCurrency',
        editable: true,
      },
      {
        headerName: t('product.draft.header.50pcs'),
        field: '50pcs',
        width: columnsWidth['50pcs'],
        type: 'USDTCurrency',
        editable: true,
      },
      {
        headerName: t('product.draft.header.100pcs'),
        field: '100pcs',
        width: columnsWidth['100pcs'],
        type: 'USDTCurrency',
        editable: true,
      },
      {
        headerName: t('product.draft.header.500pcs'),
        field: '500pcs',
        width: columnsWidth['500pcs'],
        type: 'USDTCurrency',
        editable: true,
      },
      {
        headerName: t('product.draft.header.box.size'),
        field: 'box_size',
        width: columnsWidth.box_size,
        editable: true,
        type: ['tags', 'box'],
        cellRenderer: props => (
          <TagFormatter
            data={props}
            editable
            onDeleteTag={handleUpdateProductDraft}
            field={props.colDef.field}
          />
        ),
      },
      {
        headerName: t('product.draft.header.box.weight'),
        field: 'box_weight',
        width: columnsWidth.box_weight,
        type: ['box', 'weight'],
        editable: true,
        minWidth: 150,
      },
    ],
    [categories, currentOptionsDropDownIndex], // eslint-disable-line
  );

  const getRowsData = () => {
    const rowData = [];
    gridRef.current.api.forEachNode(node => {
      if (
        node.data['10pcs'] ||
        node.data['50pcs'] ||
        node.data['100pcs'] ||
        node.data['500pcs'] ||
        node.data.EXW
      ) {
        node.data.price_type = PRICE_TYPES.FIX;
      }
      if (node.data.quantity || node.data.price) {
        node.data.price_type = PRICE_TYPES.FLEX;
      }
      rowData.push(node.data);
    });
    return rowData;
  };

  const addRows = useCallback(quantity => {
    const newItems = [...Array(quantity)].map(() => ({
      row_id: uuidv4(),
    }));
    actions.addProductDraftRows(newItems);
  }, []); // eslint-disable-line

  const handleOpenOptionsDropDown = (index, field) => () => {
    if (currentOptionsDropDownIndex[field] === index) {
      setCurrentOptionsDropDownIndex({ [field]: null });
    } else {
      setCurrentOptionsDropDownIndex({ [field]: index });
    }
  };

  const handleSelectOption = (item, props) => () => {
    const preData = getRowsData();
    const newData = preData.map(row => {
      return row.row_id === props.data.row_id
        ? { ...row, [props.colDef.field]: item }
        : row;
    });
    actions.updateProductDraft(newData);
    setCurrentOptionsDropDownIndex({ [props.colDef.field]: null });
  };

  const handleChangeProductDraft = useCallback((_, cell) => {
    const { field } = cell.colDef;
    if (field === 'EXW') {
      cell.data['10pcs'] = (cell.data.EXW * 1.28) / 6.66;
      cell.data['50pcs'] = (cell.data.EXW * 1.2) / 6.66;
      cell.data['100pcs'] = (cell.data.EXW * 1.18) / 6.66;
      cell.data['500pcs'] = (cell.data.EXW * 1.13) / 6.66;
      cell.data.price = null;
      cell.data.quantity = null;
    }
    if (
      field === '10pcs' ||
      field === '50pcs' ||
      field === '100pcs' ||
      field === '500pcs'
    ) {
      cell.data.price = null;
      cell.data.quantity = null;
    }
    if (field === 'quantity' || field === 'price') {
      cell.data['10pcs'] = null;
      cell.data['50pcs'] = null;
      cell.data['100pcs'] = null;
      cell.data['500pcs'] = null;
      cell.data.EXW = null;
    }
    actions.changeProductDraft(getRowsData());
  }, []); // eslint-disable-line

  const reFetch = () => {
    actions.getProductDraft();
  };

  const onPublish = async () => {
    try {
      await actions.publishProductDraft();
      navigate(PAGE_PATHS.PRODUCT);
    } catch (e) {
      console.error(e);
    }
  };

  const redirectToProduct = () => {
    navigate(PAGE_PATHS.PRODUCT);
  };

  const handleUpdateProductDraft = cell => {
    const preData = getRowsData();
    const newData = preData.map(item =>
      item.row_id === cell.row_id ? cell : item,
    );
    actions.updateProductDraft(newData);
  }; // eslint-disable-line

  useEffect(() => {
    actions.getProductDraft();
    actions.getAllCategories();
  }, []); // eslint-disable-line

  return (
    <>
      <PageHeader size="large">
        <PageHeaderContainer>
          <BsArrowLeft onClick={redirectToProduct} fill={colors.green} />
          <Text variant="title2">{t('product.draft.title')}</Text>
        </PageHeaderContainer>
        <Button size="medium" onClick={onPublish} variant="primary">
          {t('product.draft.publish.button')}
        </Button>
      </PageHeader>
      {productDraft && (
        <GridTable
          ref={gridRef}
          rows={productDraft}
          errors={productDraftValidationErrors}
          columnDefs={columnDefs}
          onAddItems={addRows}
          onUpdateItem={handleChangeProductDraft}
          rowSelection="multiple"
          defaultRowsNumber={10}
        />
      )}
    </>
  );
}

export default ProductDraft;
